import { Button, Stepper, toast } from "ui";
import Drawer from "../Drawer";
import { Flow } from "./types";
import { useEffect, useState } from "react";
import { ConfigurationStep, CloneStep, ReviewStep } from "./CloneFlowSteps";
import { Form, useFormRef } from "../../features/form";
import { useMutation } from "@tanstack/react-query";
import { axiosApiInstance } from "../../pages/api/axios";
import { CloneFlowPreviewResponse } from "./CloneFlowSteps/types";
import { parseErrorsToArray } from "../../utils/parseErrors";

const stepperSteps = ["Configuration", "Clone", "Review and confirm"];

const drawerTitles = (flowName: string) => [
  `Clone flow "${flowName}"`,
  "Select what to clone",
  "Review & Confirm",
];

const isLastStep = (index: number) => index === stepperSteps.length - 1;
const isFirstStep = (index: number) => index === 0;

type CloneFlowDrawerProps = {
  isOpen: boolean;
  onSuccess: (flowId: string) => void;
  onClose: () => void;
  flow: Flow;
};

export const CloneFlowDrawer = ({
  isOpen,
  onSuccess,
  onClose,
  flow,
}: CloneFlowDrawerProps) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const formRef = useFormRef();

  const cloneFlowMutation = useMutation({
    mutationFn: (payload: {
      target_account_ids: string[];
      new_flow_name: string;
      selected_routing_methods: string[];
      include_risk_rules: boolean;
      include_bin_lookup: boolean;
    }) =>
      axiosApiInstance
        .post(`/d/flows/${flow.id}/clone/`, payload)
        .then((res) => res.data.data),
    onSuccess: ({ flow_id }) => {
      onSuccess(flow_id);
      onClose();
    },
  });

  const clonePreviewMutation = useMutation({
    mutationFn: ({ accountId, name }: { accountId: string; name: string }) =>
      axiosApiInstance
        .post<{ data: CloneFlowPreviewResponse }>(
          `/d/flows/${flow.id}/clone/preview/`,
          {
            target_account_ids: [accountId],
            new_flow_name: name,
          }
        )
        .then((res) => res.data.data),
  });

  const goToNextStep = () => {
    setCurrentStepIndex((prev) => prev + 1);
  };

  const goToPreviousStep = () => {
    setCurrentStepIndex((prev) => prev - 1);
  };

  const handleCloneFlow = () => {
    const form = formRef.current;
    if (!form) return;

    const account = form.getValues("account");
    const name = form.getValues("name");
    const routingRules = form.getValues("routing_rules") || {};
    const selectedRoutingMethods = Object.entries(routingRules)
      .filter(([, selected]) => selected)
      .map(([method]) => method);

    cloneFlowMutation.mutate({
      target_account_ids: [account[0]],
      new_flow_name: name,
      selected_routing_methods: selectedRoutingMethods,
      include_risk_rules: !!form.getValues("risk_rules_enabled"),
      include_bin_lookup: !!form.getValues("bin_lookup_enabled"),
    });
  };

  const handleContinue = async () => {
    const form = formRef.current;
    if (!form) return;

    if (currentStepIndex === 0) {
      const isValid = await form.trigger(["name", "account"]);
      if (!isValid) return;

      const account = form.getValues("account");
      const name = form.getValues("name");
      clonePreviewMutation.mutate(
        { accountId: account[0], name },
        {
          onSuccess: () => goToNextStep(),
          onError: (error) => {
            const [errorMessage] = parseErrorsToArray(error);

            toast.error(errorMessage ?? "Error getting clone flow info", {
              id: "preview-clone-flow",
              duration: 4000,
            });
          },
        }
      );

      return;
    }

    if (currentStepIndex === 1) {
      const routingRules = form.getValues("routing_rules") || {};
      const hasSelectedRule = Object.values(routingRules).some(Boolean);

      if (!hasSelectedRule) {
        form.setError("routing_rules", {
          type: "manual",
          message: "At least one routing rule must be selected",
        });
        return;
      }

      form.clearErrors("routing_rules");
    }

    goToNextStep();
  };

  const handlePrimaryClick = isLastStep(currentStepIndex)
    ? handleCloneFlow
    : handleContinue;

  const handleSecondaryClick = isFirstStep(currentStepIndex)
    ? onClose
    : goToPreviousStep;

  useEffect(() => {
    if (!isOpen) setCurrentStepIndex(0);
  }, [isOpen]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={drawerTitles(flow.name)[currentStepIndex]}
      className="max-w-[545px]"
    >
      <Drawer.Content>
        <Stepper steps={stepperSteps} currentStepIndex={currentStepIndex} />

        <Form ref={formRef as any} onSubmit={() => {}} className="mt-6">
          {currentStepIndex === 0 && <ConfigurationStep flow={flow} />}
          {currentStepIndex === 1 && (
            <CloneStep
              cloneableComponents={clonePreviewMutation.data?.components}
            />
          )}
          {currentStepIndex === 2 && (
            <ReviewStep
              cloneableComponents={clonePreviewMutation.data?.components}
            />
          )}
        </Form>
      </Drawer.Content>

      <Drawer.Footer>
        <Button
          onClick={handlePrimaryClick}
          isLoading={
            clonePreviewMutation.isLoading || cloneFlowMutation.isLoading
          }
        >
          {isLastStep(currentStepIndex) ? "Clone flow" : "Continue"}
        </Button>

        <Button variant="secondary" onClick={handleSecondaryClick}>
          {isFirstStep(currentStepIndex) ? "Cancel" : "Back"}
        </Button>
      </Drawer.Footer>
    </Drawer>
  );
};
